// Utilities to handle link parsing, rendering and position maintenance

/**
 * Parse links from HTML question text and return an array with
 * { text, url, startIndex, length } relative to the plain text content.
 * This preserves the exact occurrence indexes for duplicate words.
 */
export const parseLinksFromHtml = (htmlText) => {
  if (!htmlText || typeof htmlText !== 'string') return []

  const plainText = htmlText.replace(/<[^>]*>/g, '')

  // Map HTML positions to plain text positions by walking both strings
  let plainTextCursor = 0
  let htmlCursor = 0
  const linkPositions = []

  while (htmlCursor < htmlText.length && plainTextCursor <= plainText.length) {
    if (htmlText[htmlCursor] === '<') {
      const tagEnd = htmlText.indexOf('>', htmlCursor)
      if (tagEnd === -1) break
      const tag = htmlText.slice(htmlCursor, tagEnd + 1)

      if (/^<a\s/i.test(tag)) {
        // Anchor tag; find closing tag and capture inner text
        const linkEnd = htmlText.indexOf('</a>', tagEnd + 1)
        if (linkEnd === -1) {
          htmlCursor = tagEnd + 1
          continue
        }

        const linkContent = htmlText.slice(tagEnd + 1, linkEnd)
        const urlMatch = tag.match(/href="([^"]*)"/i)
        const trimmed = (linkContent || '').replace(/<[^>]*>/g, '').trim()
        if (urlMatch && trimmed) {
          linkPositions.push({
            text: trimmed,
            url: urlMatch[1],
            startIndex: plainTextCursor,
            length: trimmed.length,
          })
          // Advance the plain cursor by the visible characters inside the link
          plainTextCursor += trimmed.length
        }
        // Skip entire anchor section in HTML
        htmlCursor = linkEnd + 4 // len('</a>')
        continue
      }

      // Other tags: skip, no visible characters
      htmlCursor = tagEnd + 1
      continue
    }

    // Normal visible character
    plainTextCursor += 1
    htmlCursor += 1
  }

  return linkPositions
}

/**
 * Adjust link positions after a text edit by computing a minimal diff between
 * previous plain text and the next plain text. This guarantees that indices
 * after the change are shifted correctly instead of re-searching globally.
 */
export const adjustLinksOnTextChange = (oldText, newText, links) => {
  if (!Array.isArray(links) || links.length === 0) return links

  const oldLen = (oldText || '').length
  const newLen = (newText || '').length

  // 1) Find longest common prefix
  let prefix = 0
  while (
    prefix < oldLen &&
    prefix < newLen &&
    oldText[prefix] === newText[prefix]
  ) {
    prefix++
  }

  // 2) Find longest common suffix (after the divergence point)
  let suffix = 0
  while (
    suffix < oldLen - prefix &&
    suffix < newLen - prefix &&
    oldText[oldLen - 1 - suffix] === newText[newLen - 1 - suffix]
  ) {
    suffix++
  }

  const oldChangeStart = prefix
  const oldChangeEnd = oldLen - suffix
  const newChangeStart = prefix
  const newChangeEnd = newLen - suffix
  const delta = newChangeEnd - newChangeStart - (oldChangeEnd - oldChangeStart)

  return links.map((link) => {
    if (
      typeof link.startIndex !== 'number' ||
      typeof link.length !== 'number'
    ) {
      return link
    }

    const linkStart = link.startIndex
    const linkEnd = link.startIndex + link.length

    // Case A: link entirely before the change region
    if (linkEnd <= oldChangeStart) {
      return link
    }

    // Case B: link entirely after the change region → shift by delta
    if (linkStart >= oldChangeEnd) {
      return { ...link, startIndex: link.startIndex + delta }
    }

    // Case C: change overlaps link region
    // Compute new start and new length depending on insertion/deletion
    const newStart = Math.min(linkStart, newChangeStart)

    // Determine how much of the link's content survived; we try to keep
    // the link spanning the same semantic segment by expanding/shrinking
    // according to delta if edit is inside the link.

    // let overlapLeft = Math.max(0, oldChangeStart - linkStart)
    // let overlapRight = Math.max(0, linkEnd - oldChangeEnd)
    // const preservedLeft =
    //   link.length -
    //   overlapLeft -
    //   Math.max(0, linkEnd - oldChangeStart - overlapRight)

    // Fallback: if computation is messy, treat as: if edit happened inside the link, extend/shrink by delta
    const editInsideLink = linkStart < oldChangeEnd && linkEnd > oldChangeStart
    const newLength = editInsideLink
      ? Math.max(0, link.length + delta)
      : link.length

    // Clamp within new text length
    const clampedStart = Math.max(0, Math.min(newStart, newLen))
    const clampedEnd = Math.max(
      clampedStart,
      Math.min(clampedStart + newLength, newLen),
    )
    const finalLength = clampedEnd - clampedStart

    return {
      ...link,
      startIndex: clampedStart,
      length: finalLength,
      text: newText.slice(clampedStart, clampedStart + finalLength),
    }
  })
}

/**
 * Render text with links using their precise positions where available.
 * Returns HTML string suitable for dangerouslySetInnerHTML.
 */
export const renderTextWithLinks = (text, links) => {
  if (!text) {
    return ''
  }

  if (!links || links.length === 0) {
    return text
  }

  const linksWithPos = links.filter(
    (l) =>
      typeof l.startIndex === 'number' &&
      typeof l.length === 'number' &&
      l.length > 0,
  )
  const linksWithoutPos = links.filter(
    (l) => !(typeof l.startIndex === 'number' && typeof l.length === 'number'),
  )

  let html = text

  if (linksWithPos.length > 0) {
    const sorted = [...linksWithPos].sort(
      (a, b) => (a.startIndex || 0) - (b.startIndex || 0),
    )
    let cursor = 0
    let buffer = ''
    sorted.forEach((l) => {
      const start = Math.max(0, Math.min(text.length, l.startIndex))
      const end = Math.max(start, Math.min(text.length, start + l.length))
      buffer += text.slice(cursor, start)
      const linkText = text.slice(start, end) || l.text || ''
      if (l.url && linkText) {
        buffer += `<a data-url="${l.url}" style="color: #bf9000; cursor: pointer;">${linkText}</a>`
      } else {
        buffer += linkText
      }
      cursor = end
    })
    buffer += text.slice(cursor)
    html = buffer
  }

  if (linksWithoutPos.length > 0) {
    linksWithoutPos.forEach((link) => {
      if (!link.text || !link.url) return
      const linkHtml = `<a data-url="${link.url}" style="color: #bf9000; cursor: pointer; text-decoration: underline;">${link.text}</a>`
      html = html.replace(link.text, linkHtml)
    })
  }

  return html
}
